<style lang="scss" scoped>
@use 'sass:color';
@use '../../palette.scss' as *;

.log-data {
    font-size: smaller;
    vertical-align: top;
    text-align: left;
}

.log-blob {
    white-space: pre-wrap;
    font-family: monospace;
    word-break: break-word;
}

.button-row {
    display: flex;
    align-items: center;
    justify-content: space-between;

    button {
        margin: 10px 20px;

        .fa-arrow-left {
            margin-right: 15px;
        }

        .fa-chevron-left.second {
            margin-right: 15px;
        }

        .fa-arrow-right {
            margin-left: 15px;
        }

        .fa-chevron-right.first {
            margin-left: 15px;
        }
    }
}

.prop {
    display: flex;
    width: 100%;
    position: relative;
    align-items: baseline;
    margin-left: 20px;
    margin-bottom: 10px;

    .input-box {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        margin-left: 10px;
    }

    .name {
        font-weight: bold;
    }

    .dropdown-container {
        min-width: 200px;
        z-index: 5;
    }
}

.message-log-table {
    table-layout: fixed;

    th,
    td {
        border: 1px solid $theme-light-grey;
    }

    thead th:nth-child(1) {
        width: 140px;
    }

    thead th:nth-child(2) {
        width: 70px;
    }

    thead th:nth-child(3) {
        width: 80px;
    }

    thead th:nth-child(4) {
        width: 120px;
    }

    thead th:nth-child(5) {
        width: 100px;
    }

    thead th:nth-child(6) {
        width: 30%;
    }

    thead th:nth-child(7) {
        width: 30%;
    }

    .collapsible {
        height: 120px;
        position: relative;
        margin: 5px;
        overflow: hidden;

        &.open {
            height: auto;

            .fade-out {
                display: none;
            }
        }

        .fade-out {
            position: absolute;
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
            background: linear-gradient(
                180deg,
                rgba(255, 255, 255, 0) 80%,
                rgba(255, 255, 255, 1) 100%
            );
        }

        .overlay {
            display: flex;
            opacity: 0;
            position: absolute;
            width: calc(100% - 10px);
            height: calc(100% - 10px);
            left: 5px;
            top: 5px;
            z-index: 5;
            background-color: color.adjust($theme-light-blue, $alpha: -0.5);
            border-radius: 5px;
            align-items: center;
            justify-content: center;

            &:hover {
                opacity: 1;
                cursor: pointer;
            }

            .label {
                color: white;
                font-size: 1em;
            }

            .copy-block {
                position: absolute;
                background-color: white;
                right: 0;
                top: 0;
                padding: 8px 10px;
                color: color.adjust($theme-light-blue, $alpha: -0.5);
                font-size: 1.4em;
                border-bottom-left-radius: 5px;
                border-top-right-radius: 5px;

                &:hover {
                    background-color: color.adjust(white, $lightness: -4%);
                    color: color.adjust($theme-light-blue, $alpha: -0.4);
                }

                &:active {
                    transform: scale(0.96);
                }
            }
        }
    }

    .delivered-icon {
        font-size: 1.3em;
        width: 100%;
        text-align: center;

        .fa-times {
            color: $theme-red;
        }

        .fa-check {
            color: $theme-green;
        }
    }
}

.copy-area {
    transform: scale(0);
}
</style>

<template>
    <Card color="blue" fullWidth noBodyPad>
        <template #header>{{ $t('messageLog') }}</template>
        <template #body>
            <div>
                <div class="button-row">
                    <Button
                        outline
                        :disabled="previousButtonDisabled"
                        @click="goToBeginning"
                    >
                        <i class="fa fa-chevron-left"></i>
                        <i class="fa fa-chevron-left second"></i>
                        {{ $t('start') }}
                    </Button>
                    <div>
                        <Button
                            outline
                            :disabled="previousButtonDisabled"
                            @click="getPreviousPage"
                        >
                            <i class="fa fa-arrow-left"></i>
                            {{ $t('prev') }}
                        </Button>
                        <Button
                            outline
                            :disabled="onLastPage"
                            @click="getNewPage"
                        >
                            {{ $t('next') }}
                            <i class="fa fa-arrow-right"></i>
                        </Button>
                    </div>
                    <Button outline :disabled="onLastPage" @click="goToEnd">
                        {{ $t('end') }}
                        <i class="fa fa-chevron-right first"></i>
                        <i class="fa fa-chevron-right"></i>
                    </Button>
                </div>
                <div class="prop">
                    <span class="name">{{ $t('filterBy') }}</span>
                    <div class="input-box">
                        <DropdownMenu
                            :options="unitOptions"
                            :maxHeight="'200px'"
                            :value="filterUnit"
                            outline
                            @change="setUnitFilter"
                        >
                        </DropdownMenu>
                    </div>
                </div>
                <table
                    class="table table-bordered table-hover table-sm message-log-table"
                >
                    <thead>
                        <tr role="row">
                            <th>{{ $t('time') }}</th>
                            <th>{{ $t('path') }}</th>
                            <th>{{ $t('direction') }}</th>
                            <th>{{ $t('unit') }}</th>
                            <th>{{ $t('delivered') }}</th>
                            <th>{{ $t('meta') }}</th>
                            <th>{{ $t('message') }}</th>
                        </tr>
                    </thead>
                    <tr v-for="log in logs" :key="log._id" class="log-data">
                        <td>{{ log.timestamp }}</td>
                        <td>{{ log.channel }}</td>
                        <td>{{ log.direction }}</td>
                        <td>{{ log.unit }}</td>
                        <td>
                            <div class="delivered-icon">
                                <i
                                    class="fas fa-check"
                                    v-if="log.delivered"
                                ></i>
                                <i class="fas fa-times" v-else></i>
                            </div>
                        </td>
                        <td>
                            <div
                                class="collapsible"
                                :class="{ open: isMessageOpen(log.id) }"
                            >
                                <div class="log-blob">{{ log.meta }}</div>
                                <div class="fade-out"></div>
                                <div
                                    class="overlay"
                                    @click="toggleMessageOpen(log.id)"
                                >
                                    <span class="label">{{
                                        isMessageOpen(log.id)
                                            ? $t('collapse')
                                            : $t('expand')
                                    }}</span>
                                    <div
                                        class="copy-block"
                                        @click.stop="copyText(log.meta)"
                                    >
                                        <i class="fas fa-copy"></i>
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td>
                            <div
                                class="collapsible"
                                :class="{ open: isMessageOpen(log.id) }"
                            >
                                <div class="log-blob">{{ log.message }}</div>
                                <div class="fade-out"></div>
                                <div
                                    class="overlay"
                                    @click="toggleMessageOpen(log.id)"
                                >
                                    <span class="label">{{
                                        isMessageOpen(log.id)
                                            ? $t('collapse')
                                            : $t('expand')
                                    }}</span>
                                    <div
                                        class="copy-block"
                                        @click.stop="copyText(log.message)"
                                    >
                                        <i class="fas fa-copy"></i>
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                </table>
                <div class="button-row">
                    <Button
                        outline
                        :disabled="previousButtonDisabled"
                        @click="goToBeginning"
                    >
                        <i class="fa fa-chevron-left"></i>
                        <i class="fa fa-chevron-left second"></i>
                        Start
                    </Button>
                    <div>
                        <Button
                            outline
                            :disabled="previousButtonDisabled"
                            @click="getPreviousPage"
                        >
                            <i class="fa fa-arrow-left"></i>
                            Prev
                        </Button>
                        <Button
                            outline
                            :disabled="onLastPage"
                            @click="getNewPage"
                        >
                            Next
                            <i class="fa fa-arrow-right"></i>
                        </Button>
                    </div>
                    <Button outline :disabled="onLastPage" @click="goToEnd">
                        End
                        <i class="fa fa-chevron-right first"></i>
                        <i class="fa fa-chevron-right"></i>
                    </Button>
                </div>
            </div>
        </template>
    </Card>
</template>

<script>
import i18next from 'i18next';
import { mapGetters } from 'vuex';
import * as client from '../../api/client';
import { getUnitsList } from '../../api/units';
import { changeMonitor } from '../../api/ChangeMonitor';
import toastr from 'toastr';

export function formatMessageLogs(rawLogs, timeFormatter) {
    const elements = [];

    for (const log of rawLogs) {
        const e = {};
        e.channel = log.channel;
        e.direction = log.direction;
        e.timestamp = timeFormatter.format(new Date(log.timestamp));
        e.unit = log.unit_name;
        e.unit_id = log.unit_id;
        e.meta = JSON.stringify(log.meta, null, ' ');
        e.id = log.id;
        e.message = JSON.stringify(log.message, null, ' ');
        e.delivered = log.delivered ? log.delivered : false;

        elements.push(e);
    }

    return elements;
}

export default {
    data() {
        return {
            rawLogs: [],
            unitOptions: [],
            openMessages: [],
            filterUnit: null,
            logsRequestId: 0,
            logsAbortController: null,
            incomingShiftQueue: 0,
            isProcessingIncomingShift: false,
            pageSize: 20, // Customizable, can be changed as desired later
            firstIndex: -1,
            prevIndex: -1,
            nextIndex: -1,
            onLastPage: false,
        };
    },

    computed: {
        ...mapGetters(['isUserAdmin']),

        logs() {
            return formatMessageLogs(
                this.rawLogs,
                this.$store.getters.timeFormatter,
            );
        },

        unitQueryParam() {
            return this.filterUnit != null ? `unit=${this.filterUnit}` : '';
        },

        previousButtonDisabled() {
            return this.prevIndex >= this.firstIndex;
        },

        beginPageTimestamp() {
            if (this.rawLogs.length > 0) {
                // The smallest timestamp will be at the end of the page since we're viewing them in descending.
                const posix = new Date(
                    this.rawLogs[this.rawLogs.length - 1].timestamp,
                ).valueOf();
                return Number.isNaN(posix) ? 0 : posix;
            }
            return 0;
        },
    },

    created: async function () {
        // Fetch messages and units list in parallel to reduce load time
        const [data, response] = await Promise.all([
            client.get('/logs/message'),
            getUnitsList(),
        ]);

        // Get messages from server
        this.rawLogs = data.payload;
        if (data.payload && data.payload.length > 0) {
            this.firstIndex = this.normalizeLogId(data.payload[0].id);
            this.prevIndex = this.firstIndex;
            this.nextIndex = this.normalizeLogId(
                data.payload[data.payload.length - 1].id,
            );
        }
        if (data.payload.length < this.pageSize) {
            this.onLastPage = true;
        }

        // Populate our units dropdown list
        if (response) {
            const units = response.payload;
            //    if(this.isUserAdmin) {
            this.unitOptions.push({
                label: i18next.t('none'),
                value: null,
            });
            //     }
            units.forEach((unit) => {
                this.unitOptions.push({
                    label: unit.name,
                    value: unit.id,
                });
            });
        }

        changeMonitor.addNotifiedUser(this.handleChanges);
    },

    methods: {
        normalizeLogId(idValue) {
            const id = Number(idValue);
            return Number.isNaN(id) ? -1 : id;
        },

        pruneOpenMessages() {
            if (!this.openMessages.length || !this.rawLogs?.length) {
                return;
            }

            const visibleLogIds = new Set(
                this.rawLogs.map((log) => this.normalizeLogId(log.id)),
            );
            this.openMessages = this.openMessages.filter((id) =>
                visibleLogIds.has(this.normalizeLogId(id)),
            );
        },

        normalizeFilterUnitValue(value) {
            if (value === null || value === '') {
                return null;
            }

            const numericValue = Number(value);
            return Number.isNaN(numericValue) ? value : numericValue;
        },

        isMatchingFilterUnit(unitId) {
            if (this.filterUnit === null) {
                return true;
            }

            return String(unitId) === String(this.filterUnit);
        },

        buildLogsPath(extraQueryParam = '') {
            const params = [`limit=${this.pageSize}`];
            if (extraQueryParam) {
                params.push(extraQueryParam);
            }
            if (this.filterUnit != null) {
                params.push(`unit=${encodeURIComponent(this.filterUnit)}`);
            }

            return `/logs/message?${params.join('&')}`;
        },

        async fetchLogsPage(extraQueryParam = '') {
            const requestId = ++this.logsRequestId;
            if (this.logsAbortController) {
                this.logsAbortController.abort();
            }

            const controller = new AbortController();
            this.logsAbortController = controller;
            try {
                const data = await client.get(
                    this.buildLogsPath(extraQueryParam),
                    controller,
                );

                if (data?.msg === 'requestAborted') {
                    return null;
                }

                // Ignore stale responses so older requests do not overwrite new filter/page state.
                if (requestId !== this.logsRequestId) {
                    return null;
                }

                return data;
            } finally {
                if (this.logsAbortController === controller) {
                    this.logsAbortController = null;
                }
            }
        },

        async processIncomingShiftQueue() {
            if (this.isProcessingIncomingShift) {
                return;
            }

            this.isProcessingIncomingShift = true;
            const activeFilter = this.filterUnit;
            try {
                while (this.incomingShiftQueue > 0) {
                    this.incomingShiftQueue -= 1;

                    // If the user returned to first page or changed filters, stop shifting.
                    if (
                        this.previousButtonDisabled ||
                        String(activeFilter) !== String(this.filterUnit)
                    ) {
                        break;
                    }

                    if (!this.rawLogs.length) {
                        continue;
                    }

                    // Keep browsing position stable by sliding the page window down.
                    this.rawLogs.shift();
                    const oldestId = this.normalizeLogId(
                        this.rawLogs[this.rawLogs.length - 1]?.id,
                    );
                    if (oldestId < 0) {
                        continue;
                    }

                    const unitParam =
                        this.filterUnit != null
                            ? `&unit=${encodeURIComponent(this.filterUnit)}`
                            : '';
                    const data = await client.get(
                        `/logs/message?limit=1&next=${oldestId}${unitParam}`,
                    );

                    if (data?.payload?.length) {
                        this.rawLogs.push(data.payload[0]);
                    }

                    if (this.rawLogs.length) {
                        this.prevIndex = this.normalizeLogId(
                            this.rawLogs[0].id,
                        );
                        this.nextIndex = this.normalizeLogId(
                            this.rawLogs[this.rawLogs.length - 1].id,
                        );
                    }
                    this.pruneOpenMessages();
                }
            } finally {
                this.isProcessingIncomingShift = false;
            }
        },

        async handleChanges(changeRecord) {
            if (changeRecord.changeType === 'MESSAGE_LOG') {
                const posix = new Date(changeRecord.data.timestamp).valueOf();
                // If we have logs and this timestamp doesn't fall in the range, we dump it
                if (this.logs.length > 0 && posix < this.beginPageTimestamp) {
                    return;
                }
                // Otherwise if we already have this message, update it
                if (this.rawLogs) {
                    for (let i = 0; i < this.rawLogs.length; i++) {
                        if (this.rawLogs[i].id === changeRecord.data.id) {
                            // We have an existing message that needs an update
                            this.rawLogs.splice(i, 1, changeRecord.data);
                            return;
                        }
                    }

                    // Otherwise check to see if it should be added to our list
                    if (this.isMatchingFilterUnit(changeRecord.data.unit_id)) {
                        if (this.previousButtonDisabled) {
                            this.rawLogs.unshift(changeRecord.data);
                            if (this.rawLogs.length > this.pageSize) {
                                this.rawLogs.splice(this.pageSize);
                            }
                            this.prevIndex = this.normalizeLogId(
                                this.rawLogs[0].id,
                            );
                            this.firstIndex = this.prevIndex;
                            this.nextIndex = this.normalizeLogId(
                                this.rawLogs[this.rawLogs.length - 1].id,
                            );
                            this.pruneOpenMessages();
                        } else {
                            this.incomingShiftQueue += 1;
                            await this.processIncomingShiftQueue();
                        }
                    }
                } else {
                    if (this.isMatchingFilterUnit(changeRecord.data.unit_id)) {
                        this.rawLogs = [changeRecord.data];
                        this.prevIndex = this.normalizeLogId(
                            this.rawLogs[0].id,
                        );
                        this.firstIndex = this.prevIndex;
                        this.nextIndex = this.prevIndex;
                    }
                }
            }
        },

        async getPreviousPage() {
            const data = await this.fetchLogsPage(`prev=${this.prevIndex}`);
            if (!data) {
                return;
            }

            this.rawLogs = data.payload;
            this.pruneOpenMessages();
            // We'll always have a previous page if we make this call, if we don't the button was disabled
            this.prevIndex = this.normalizeLogId(data.payload[0].id);
            this.nextIndex = this.normalizeLogId(
                data.payload[data.payload.length - 1].id,
            );
            // It's impossible to go backwards from another page and be on the last page
            this.onLastPage = false;

            this.scrollToTop();
        },

        async getNewPage() {
            const data = await this.fetchLogsPage(`next=${this.nextIndex}`);
            if (!data) {
                return;
            }

            this.rawLogs = data.payload;
            this.pruneOpenMessages();
            // We may or may not get any results, set previous index first to allow us to go back
            this.prevIndex = this.nextIndex - 1;
            if (data.payload && data.payload.length > 0) {
                this.nextIndex = this.normalizeLogId(
                    data.payload[data.payload.length - 1].id,
                );
            }

            // If we didn't get a full page or nothing at all, we're done, flag it
            if (
                !data.payload.length > 0 ||
                data.payload.length < this.pageSize
            ) {
                this.onLastPage = true;
            } else {
                this.onLastPage = false;
            }

            this.scrollToTop();
        },

        async goToBeginning() {
            const data = await this.fetchLogsPage();
            if (!data) {
                return;
            }

            if (data.payload.length) {
                this.rawLogs = data.payload;
                this.pruneOpenMessages();
                this.firstIndex = this.normalizeLogId(data.payload[0].id);
                this.prevIndex = this.firstIndex;
                this.nextIndex = this.normalizeLogId(
                    data.payload[data.payload.length - 1].id,
                );
                this.onLastPage = data.payload.length < this.pageSize;
            } else {
                this.rawLogs = [];
                this.prevIndex = -1;
                this.firstIndex = -1;
                this.nextIndex = -1;
                this.onLastPage = true;
            }

            this.scrollToTop();
        },

        async goToEnd() {
            const data = await this.fetchLogsPage('prev=0');
            if (!data) {
                return;
            }

            if (data.payload.length) {
                this.rawLogs = data.payload;
                this.pruneOpenMessages();
                // Keep firstIndex anchored to the newest-page top id so Prev/Start stay enabled at the end.
                if (this.firstIndex < 0) {
                    this.firstIndex = this.normalizeLogId(data.payload[0].id);
                }
                this.prevIndex = this.normalizeLogId(data.payload[0].id);
                this.nextIndex = this.normalizeLogId(
                    data.payload[data.payload.length - 1].id,
                );
                this.onLastPage = true;
            } else {
                this.rawLogs = [];
                this.prevIndex = -1;
                this.firstIndex = -1;
                this.nextIndex = -1;
                this.onLastPage = true;
            }

            this.scrollToTop();
        },

        scrollToTop() {
            document.getElementById('app-main').scrollTo(0, 0);
        },

        setUnitFilter(value) {
            const nextFilterUnit = this.normalizeFilterUnitValue(value);
            if (String(nextFilterUnit) === String(this.filterUnit)) {
                return;
            }

            this.filterUnit = nextFilterUnit;
            this.openMessages = [];
            this.rawLogs = [];
            this.prevIndex = -1;
            this.firstIndex = -1;
            this.nextIndex = -1;
            this.onLastPage = true;
            this.goToBeginning();
        },

        toggleMessageOpen(id) {
            const index = this.openMessages.indexOf(id);
            if (index > -1) {
                this.openMessages.splice(index, 1);
            } else {
                this.openMessages.push(id);
            }
        },

        isMessageOpen(id) {
            return this.openMessages.includes(id);
        },

        copyText(text) {
            const copyBox = document.createElement('textarea');
            copyBox.value = text;
            document.body.appendChild(copyBox);
            copyBox.select();
            document.execCommand('copy');
            document.body.removeChild(copyBox);
            toastr.info(this.$t('contentsCopied'), null, { timeOut: 1000 });
        },
    },

    beforeDestroy: function () {
        if (this.logsAbortController) {
            this.logsAbortController.abort();
        }
        changeMonitor.removeNotifiedUser(this.handleChanges);
    },
};
</script>
